import { parseDate } from '@internationalized/date';
import type { IconName } from 'lucide-react/dynamic';
import {
  DateInput as AriaDateInput,
  DateField,
  DateSegment,
} from 'react-aria-components';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

interface DateInputProps {
  /** Data em formato `YYYY-MM-DD` (o mesmo formato aceito por `<input type="date">`). */
  value?: string | null;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  icon?: IconName;
  className?: string;
  'aria-label'?: string;
}

function safeParseDate(value?: string | null) {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

/**
 * Campo de data por segmentos (dia/mês/ano independentes), via react-aria.
 * Diferente de `<input type="date">` controlado, digitar um segmento incompleto
 * não dispara `onChange` com valor vazio — então o campo nunca se autozera no
 * meio da digitação do ano.
 */
export function DateInput({
  value,
  onChange,
  disabled,
  icon,
  className,
  ...props
}: DateInputProps) {
  return (
    <DateField
      aria-label={props['aria-label'] ?? 'Data'}
      isDisabled={disabled}
      value={safeParseDate(value)}
      onChange={(date) => onChange?.(date ? date.toString() : undefined)}
      className="w-full"
    >
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
            <Icon name={icon} size={16} />
          </span>
        )}
        <AriaDateInput
          className={cn(
            'relative flex h-11 w-full items-center gap-0.5 rounded-[12px] border border-border bg-raised text-[13.5px] font-mono text-foreground outline-none transition-colors',
            icon ? 'pl-10' : 'pl-3.5',
            'pr-3.5',
            'hover:border-border-strong',
            'data-[focus-within]:border-primary data-[focus-within]:shadow-[0_0_0_4px_var(--primary-soft)]',
            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60',
            className,
          )}
        >
          {(segment) => (
            <DateSegment
              segment={segment}
              className={cn(
                'rounded px-0.5 tabular-nums text-foreground caret-transparent outline-none transition-colors',
                'data-[placeholder]:text-faint',
                'data-[type=literal]:px-0 data-[type=literal]:text-faint',
                'data-[focused]:bg-primary-soft data-[focused]:text-primary',
                'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
              )}
            />
          )}
        </AriaDateInput>
      </div>
    </DateField>
  );
}
